<template>
  <ThreeColumn :breadcrumbPaths="paths">
    <template v-slot:title>
      <h4 class="text-h4 mb-0">{{title}}</h4>
    </template>
    
    <template v-slot:leftPanel>
      <!--Case Info Card-->
      <InfoTable
        :titleText="$t('database.wdg_doc')"
        :tableContent="infoTableContent"
        :contentType="content_type"
      />

      <!--Reading Options Card-->
      <LangOpts
        class="d-print-none lside-wdgt"
        v-if="has_translation"
        :options="lang_options"
        :curLang="lang"
        @go_trans="go_trans($event)"
      />

      <DownloadWidget
        v-if="status_okay && pdf"
        class="d-print-none lside-wdgt"
        :doc="pdf"
        doctype=""
        docname="PDF"
        :printable="Boolean(content)"
      />
    </template>

    <template v-slot:mainPanel>
      <v-divider></v-divider>

      <div v-if="content" class="case-content mt-7 mb-16" 
        v-html="show_hl&&$route.query.hl?content_hl:content" @click="content_clicked"
      ></div>
      
      <v-col class="mt-2 px-0 mb-16" v-if="pdf">
        <iframe class="pdf-loader" :src="pdf" target="_parent" :class="$vuetify.breakpoint.xs?'mb-16':''"  ></iframe>
      </v-col>

      <v-alert v-if="!overlay && !status_okay"
        dense text
        type="error"
        class="mt-3 mb-4"
        icon="mdi-alert-outline"
      > 
        {{$t('error.page_unavailable')}}
      </v-alert>

      <!--loading overlay-->
      <v-overlay :value="overlay">
        <v-progress-circular
          indeterminate
          size="64"
        ></v-progress-circular>
      </v-overlay>
    </template>

  </ThreeColumn>
</template>

<script>
  import ThreeColumn from '@/components/applayout/ThreeColumn'
  import InfoTable from '../components/InfoTable'
  import LangOpts from '../components/LangOpts'
  import DownloadWidget from '../components/DownloadWidget'
  import dbs_simplified from '../data/dbs_simplified.json'
  import api from '@/scripts/api'
  import datehelper from '@/scripts/datehelper'
  import scrollhelper from '@/scripts/scrollhelper'
  import highlighthelper from '@/scripts/highlighthelper'
  export default {
    name:'PDF',  // pdf + other, which may not be pdf
    components:{
      ThreeColumn,
      InfoTable,
      LangOpts,
      DownloadWidget
    },
    data(){
      return {
        overlay: false,
        status_okay: false,
        abbr: '',
        content_type: '',
        title: '',
        neutral: '',
        date: '',
        db_name: null,
        pdf: null,
        show_hl: true,
        content: null,
        hl_content: null,
        has_translation: false
      }
    },
    computed:{
      lang(){
        return this.$route.params.lang
      },
      paths(){
        if (this.status_okay){
          return [
            {text: 'HKLII', disabled: false, href: {name: 'home'}},
            {text: this.$i18n.t('database.dbs'), disabled: false, href: {name: 'databases'}},
            {text: this.db_name, disabled: false, href: this.$route.path.split('/').splice(0,4).join('/')+'/'},
            {text: this.neutral, disabled: true}
          ]
        } else {
          return [
            {text: 'HKLII', disabled: false, href: {name: 'home'}},
            {text: this.$i18n.t('database.dbs'), disabled: false, href: '/databases'},
          ]
        }
      },
      infoTableContent(){
          return [
              {name: this.$t('database.date'), value: this.date},
              {name: this.$t('database.wdg_jdg_neutral'), value: this.neutral}
          ]
      },
      lang_options(){
        return dbs_simplified
          .filter(db => db.abbr == this.abbr)
          .map(db => db.lang.toLowerCase())
      }
    },
    methods:{
      content_clicked: scrollhelper.content_clicked,
      initialize(){
        // compute abbr and content_type
        var name = this.$route.name
        if (name == 'ukpc-pdf'){
          this.abbr = 'ukpc'
          this.content_type = 'l_case'
        } else if (name == 'histlaw-pdf'){
          this.abbr = 'hkhistlaws'
          this.content_type = 'l_legis'
        } else {
          this.abbr = this.$route.params.abbr
          this.content_type = 'l_other'
        }
      },
      go_trans(lang){
        var params = {...this.$route.params, 'lang': lang}
        this.$router.push({name: 'pdf', params: params})
        this.get_file()
      },
      async get_file(){
        const params = this.$route.params
        this.overlay = true
        this.initialize()
        try {
          var res
          if(this.abbr === 'hkhistlaws'){
            res = await api.get_histlaw(this.lang, this.abbr, params.year, params.num)
          } else {
            res = await api.get_other(this.lang, this.abbr, params.year, params.num)
          }
          const file = res
          this.title = file['title']
          this.date = datehelper.display_date(file['date'])
          this.pdf = file['pdf']
          this.neutral = file['neutral']
          this.content = file['content']
          if (this.$route.query.hl && this.content){
            this.content_hl = highlighthelper.hlBody(this.content, this.$route.query.hl)
          }
          this.db_name = file['db']['name']
          this.has_translation = file['has_translation']
          //window.document.title = this.title
          
          this.status_okay = true
          await this.$nextTick()
          scrollhelper.scroll_to_anchor(this.$route.hash)
        } catch(err) {
          console.error(err)
          this.status_okay = false
        } finally {
          this.overlay = false
        }
      }
    },
    watch:{
      '$route': function(){
        this.get_file()
      }
    },
    mounted(){
      this.get_file()
    },
    updated(){
      this.$nextTick(() => {
        scrollhelper.scroll_to_anchor(this.$route.hash)
      })
    },
    metaInfo(){
      return{
        title: this.title,
      }
    },
  }
</script>

<style scoped lang="scss">
  @import '@/sass/_typography.scss';

  .pdf-loader {
    width: 100%;
    height: 800px;
    display: block;
    @media print{
      display: none;
    }
  }

  .case-content {
    @media print{
      font-size: 9.5pt;
    }
  }

  .case-content ::v-deep div.make-database > object[type="application/pdf"]{
    display: none;
  }

  .case-content ::v-deep a {
    @include link-no-underline( var(--v-link-base) );
  }

  /*****ukpc formatting*****/
  .case-content ::v-deep p{ /*body text*/
    margin-bottom: 0px;
  }
  .case-content ::v-deep .MsoNormal, .case-content ::v-deep span[style*='mso']{ /*quotes? [1997] UKPC 40 */
    font-family: 'Noto Serif' !important ;
    letter-spacing: 0pt  !important;
    padding-top: 0.5em;
    @media print{
      font-size: 9pt;
    }
  }

  .case-content ::v-deep p[style*='tab-stops']{  /*extra spaces between paragraphs*/
    margin: 0px;
  }

</style>

